import { infiniteQueryOptions, useQueryClient } from '@tanstack/react-query';

import { ContractState } from '../../types/ContractMessage';
import { ComboBoxOption } from '../cdl/ComboBox/ComboBox';
import { ASYNC_OPTIONS_STALE_TIME, EMPTY_OPTIONS_SEARCH } from '../cdl/ComboBox/utils/ComboBoxConstants';
import { getContracts, GetContractsParams } from '../common/api/clients/contracts.api';
import { queries } from '../common/api/queryKeys/queries';

interface UseDefaultContractOptionsParams {
    customerId?: string;
    supplierId?: string;
    vesselId?: string;
}

const getContractsParams = (
    filters: UseDefaultContractOptionsParams,
    searchQuery: string,
    page: number
): GetContractsParams => ({
    page,
    customerIds: filters.customerId ? [filters.customerId] : undefined,
    supplierIds: filters.supplierId ? [filters.supplierId] : [],
    states: [ContractState.ACTIVE],
    vesselIds: filters.vesselId ? [filters.vesselId] : undefined,
    searchQuery,
});

const loadDefaultContractOptions = async (filters: UseDefaultContractOptionsParams, search: string, page: number) => {
    const contractsPage = await getContracts(getContractsParams(filters, search, page));
    const contracts = [...contractsPage.content].sort((contract, otherContract) =>
        contract.name.localeCompare(otherContract.name)
    );

    return {
        ...contractsPage,
        content: contracts.map<ComboBoxOption>((contract) => ({
            value: contract.id,
            label: contract.name,
        })),
    };
};

const getDefaultContractOptionsQueryKey = (filters: UseDefaultContractOptionsParams, search: string) =>
    queries.contracts.comboBoxOptions(getContractsParams(filters, search, 0)).queryKey;

const getDefaultContractOptionsQuery = (filters: UseDefaultContractOptionsParams, search = EMPTY_OPTIONS_SEARCH) =>
    infiniteQueryOptions({
        queryKey: getDefaultContractOptionsQueryKey(filters, search),
        queryFn: ({ pageParam }) => loadDefaultContractOptions(filters, search, Number(pageParam)),
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.pageable.page + 1;
            const loadedOptionCount = nextPage * lastPage.pageable.size;

            return loadedOptionCount < lastPage.total ? nextPage : undefined;
        },
        staleTime: ASYNC_OPTIONS_STALE_TIME,
    });

export const useDefaultContractOptions = ({ customerId, supplierId, vesselId }: UseDefaultContractOptionsParams) => {
    const queryClient = useQueryClient();
    const filters = { customerId, supplierId, vesselId };

    return {
        optionsQueryKey: (search: string) => getDefaultContractOptionsQueryKey(filters, search),
        optionsQueryFn: ({ search, page }: { search: string; page: number }) =>
            loadDefaultContractOptions(filters, search, page),
        fetchFirstOptionsPage: (selectedSupplierId: string) =>
            queryClient.fetchInfiniteQuery(
                getDefaultContractOptionsQuery({ customerId, supplierId: selectedSupplierId, vesselId })
            ),
    };
};
